import React, { useContext, useEffect, useRef, useState } from "react";
import Quill, { Delta } from "quill";
import "quill/dist/quill.snow.css";
import "./TextEditor.css";

import FileUploadImg from "../../../assets/fileUpload.svg";
import FullScreen from "../../../assets/loopMaxMin.svg";
import VideoImg from "../../../assets/iconVideo.svg";
import UserMentionImg from "../../../assets/userMention.svg";
import ImageCustomImg from "../../../assets/imageCustom.svg";

import LineBlotCustom from "./CustomBlot/LineBlotCustom";
import VideoBlotCustom from "./CustomBlot/VideoBlotCustom";
import FileBlotCustom from "./CustomBlot/FileBlotCustom";
import MentionBlotCustom, { MentionData } from "./CustomBlot/MentionBlotCustom";
import ImageBlotCustom from "./CustomBlot/ImageBlotCustom";

import { AppContext } from "../../../App";
import { Context } from "../../../index";
import UserMention from "../UserMention/UserMention";
import { getFileUrlFromId } from "../../../helpers/getFileUrlFromId";
import {
    createArgumentIfDefined,
    getOptionsQueryParams,
    IArgument,
    IOptionsQueryParams,
    urlClear,
    urlFiller,
} from "../../../helpers/paramsAutoFiller";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IFullUserInfo } from "../../../common/api_interfaces/user/IUserInfo";

Quill.register(MentionBlotCustom);
Quill.register(VideoBlotCustom);
Quill.register(LineBlotCustom);
Quill.register("formats/fileUpload", FileBlotCustom);
Quill.register("formats/imageCustom", ImageBlotCustom);

const MAX_TEXT = 100000;

export interface ITextEditorProps {
    workMode: "edit" | "create" | "editor";
    callBackSave: (content: string, mention: number[]) => Promise<any> | void;
    callBackClose?: () => void;
    mention: {
        projectId: number;
    };
    editContent?: string;
    chatId?: number;
    projectId?: number;
    userAcess: {
        edit: boolean;
        create: boolean;
    };
    optionsQueryParams: IOptionsQueryParams;
    canSendEmptyMessage?: boolean;
    textLimit?: (value: boolean) => void;
    textLength?: (value: number) => void;
    disabled?: boolean;
}

const TextEditor: React.FC<ITextEditorProps> = ({
    workMode,
    callBackSave,
    callBackClose,
    mention,
    editContent,
    chatId,
    projectId,
    optionsQueryParams,
    userAcess,
    canSendEmptyMessage,
    textLimit,
    textLength,
    disabled,
}) => {
    const { store } = useContext(Context);
    const [fullShow, setFullShow] = React.useState<boolean>(false);
    const [showChooseUser, setShowChooseUser] = React.useState<boolean>(false);
    const [content, setContent] = React.useState<string>(
        editContent && (workMode === "edit" || workMode === "editor") ? editContent : ""
    );
    const [mode, setMode] = React.useState<"edit" | "create" | "editor">(workMode);
    const [saveContent, setSaveContent] = React.useState<string>("");
    const [mentionFilter, setMentionFilter] = React.useState<string>("");
    const [addFile, setAddFile] = React.useState<boolean>(false);
    const [checkAddFile, setCheckAddFile] = React.useState<number>(0);
    const [cursorPosition, setCursorPosition] = React.useState({ line: 0, character: 0 });
    const [isDragOver, setIsDragOver] = React.useState(false);
    const [usersMention, setUsersMention] = React.useState<number[]>([]);
    const [maxText, setMaxText] = useState<boolean>(false);
    const [curTextLength, setCurTextLength] = useState<number>(0);

    const editorRef = useRef<HTMLDivElement>(null);
    const quillInstanceRef = useRef<Quill | null>(null);
    const navigate = useNavigate();

    const { showToast, handleShowLoader } = useContext(AppContext);

    const [isCtrlPressed, setIsCtrlPressed] = React.useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // Инициализация Quill
    useEffect(() => {
        if (!editorRef.current) return;

        // Подгружаем иконки для селектора
        document.documentElement.style.setProperty("--file-upload-icon", `url(${FileUploadImg})`);
        document.documentElement.style.setProperty("--full-screen-icon", `url(${FullScreen})`);
        document.documentElement.style.setProperty("--video-img-icon", `url(${VideoImg})`);
        document.documentElement.style.setProperty("--mention-user-icon", `url(${UserMentionImg})`);
        document.documentElement.style.setProperty("--image-custom-icon", `url(${ImageCustomImg})`);

        // Инициализация редактора Quill
        const quill = new Quill(editorRef.current, {
            theme: "snow",
            modules: {
                toolbar: {
                    container: [
                        [{ header: [1, 2, 3, 4, 5, false] }],
                        [
                            "bold",
                            "italic",
                            "underline",
                            "strike",
                            {
                                background: [
                                    "var(--color-column-1)",
                                    "var(--color-column-2)",
                                    "var(--color-column-3)",
                                    "var(--color-column-4)",
                                    "var(--color-column-5)",
                                    "var(--color-column-6)",
                                    "var(--color-column-7)",
                                    "var(--color-column-8)",
                                    "var(--color-column-9)",
                                    "var(--color-column-10)",
                                    "transparent",
                                ],
                            },
                            {
                                color: [
                                    "var(--base-color-picker-1)",
                                    "var(--base-color-picker-2)",
                                    "var(--base-color-picker-3)",
                                    "var(--base-color-picker-4)",
                                    "var(--base-color-picker-5)",
                                    "var(--base-color-picker-6)",
                                    "var(--base-color-picker-7)",
                                    "var(--base-color-picker-8)",
                                    "var(--base-color-picker-9)",
                                    "var(--base-color-picker-10)",
                                    "black",
                                    "transparent",
                                ],
                            },
                        ],
                        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
                        ["link", "code-block", "mention"],
                        ["imageCustom", "fileUpload"],
                        ["fullScreen"],
                    ],
                    handlers: {
                        fileUpload: () => handleFileSelection(),
                        videoEmbed: () => handleInsertVideo(),
                        fullScreen: handleFocus,
                        mention: () => handleMentionPerson(),
                        imageCustom: handleImageSelection,
                        link: () => {
                            const quill = quillInstanceRef.current;
                            if (!quill) return;

                            const range = quill.getSelection();
                            if (!range) return;

                            const formats = quill.getFormat(range);

                            let currentUrlValue = "";
                            const url = prompt("Введите URL:", "https://");

                            if (url === null) return;

                            if (range.length > 0) {
                                if (url === "") {
                                    quill.format("link", false);
                                } else {
                                    quill.format("link", url);
                                }
                            } else {
                                const text = prompt("Текст ссылки:", "Ссылка");
                                if (text !== null) {
                                    quill.insertText(range.index, text || "Ссылка", "link", url || "#");
                                    quill.setSelection(range.index + (text?.length || 6), 0);
                                }
                            }
                        },
                    },
                },
                keyboard: {
                    bindings: {
                        bold: {
                            key: "b",
                            metaKey: true,
                            shortKey: false,
                            shiftKey: false,
                            handler: (range: any, context: any) => {
                                const quill = context.quill;
                                quill.format("bold", !quill.format("bold", range));
                            },
                        },
                    },
                },
                clipboard: {},
                history: {
                    delay: 1000,
                    maxStack: 200,
                    userOnly: true,
                },
            },
            formats: [
                "background",
                "bold",
                "italic",
                "underline",
                "strike",
                "header",
                "list",
                "link",
                "videoEmbed",
                "code-block",
                "color",
                "background",
                "indent",
                "fileUpload",
                "mention",
                "imageCustom",
            ],
            placeholder: "Введите текст...",
        });

        quillInstanceRef.current = quill;

        let initialContent: string = "";
        let storeQuill = store.getBlockTextQuill(optionsQueryParams, window.location.pathname);

        if (workMode === "editor" && editContent) {
            initialContent = editContent;
        } else if (
            storeQuill?.text !== "<p><br></p>" &&
            storeQuill?.text?.length &&
            editContent?.length &&
            storeQuill.text !== editContent
        ) {
            initialContent = storeQuill.text;
        } else {
            initialContent = editContent || "";
        }

        setTimeout(() => {
            if (initialContent) {
                const delta = quill.clipboard.convert({ html: initialContent });
                quill.setContents(delta, "silent");
                setContent(initialContent);
                chekLengthText(initialContent);
            }
        }, 2);

        const toolbarsAll = document.querySelectorAll(".text-editor_container");
        toolbarsAll.forEach((toolbar, index) => {
            const autoToolbars = toolbar.querySelectorAll(".ql-toolbar");
            autoToolbars.forEach((toolbarPsevdo, index) => {
                if (!toolbarPsevdo.contains(editorRef.current) && index !== autoToolbars.length - 1) {
                    toolbarPsevdo.remove();
                }
            });
        });

        if (editContent && workMode === "editor") {
            setTimeout(() => {
                chekLengthText(editContent);
                const delta = quill.clipboard.convert({ html: editContent });
                quill.setContents(delta, "silent");
                setContent(editContent);
            }, 2);
        }

        setMode(workMode);
        if (workMode === "edit") {
            setFullShow(true);
        }

        quill.on("text-change", () => {
            const html = editorRef.current?.querySelector(".ql-editor")?.innerHTML || "";
            handleChange(html);
        });

        quill.on("selection-change", (range) => {
            if (range) {
                checkMentionInText(quill);
            }
        });

        setTimeout(() => {
            if (initialContent) {
                chekLengthText(initialContent);
                const delta = quill.clipboard.convert({ html: initialContent });
                quill.setContents(delta, "silent");
                setContent(initialContent);
            }

            quill.blur();
            quill.setSelection(null);

            const editorElement = editorRef.current?.querySelector(".ql-editor");
            if (editorElement) {
                (editorElement as HTMLElement).blur();
            }
            editorRef.current?.blur();
        }, 10);

        return () => {
            quill.off("text-change");
            quill.off("selection-change");
        };
    }, [workMode, editContent, editorRef]);

    useEffect(() => {
        if (!quillInstanceRef.current) return;

        if (disabled) {
            quillInstanceRef.current.disable();
        } else {
            quillInstanceRef.current.enable();
        }
    }, [disabled]);

    const chekLengthText = (text: string) => {
        const cleanText = text.replace(/<[^>]*>|&nbsp;/g, "");

        setCurTextLength(cleanText.length);
        textLength && textLength(cleanText.length);

        if (cleanText.length > MAX_TEXT) {
            setMaxText(true);
            textLimit && textLimit(true);
        } else {
            setMaxText(false);
            textLimit && textLimit(false);
        }
    };

    useEffect(() => {
        let options = getOptionsQueryParams(window.location.href);

        if (options.entity === "chat" && options.action === "create" && editorRef.current) {
            editorRef.current.focus();
        }
    }, []);

    // Отслеживание событий с клавиатуры
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey) {
                setIsCtrlPressed(true);
            }
        };

        const handleKeyUp = () => {
            setIsCtrlPressed(false);
        };

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
        };
    }, []);

    // События вставки в редактор
    useEffect(() => {
        const editorElement = editorRef.current;
        if (!editorElement) return;

        const handlePaste = (event: ClipboardEvent) => {
            if (!isCtrlPressed) return;
            handlePasteContent(event);
        };

        editorElement.addEventListener("paste", handlePaste);
        return () => {
            editorElement.removeEventListener("paste", handlePaste);
        };
    }, [isCtrlPressed]);

    // Проверка упоминания пользователя
    const checkMentionInText = (quill: Quill) => {
        const cursorIndex = quill.getSelection();
        const text = quill.getText();

        if (!cursorIndex) {
            return;
        }

        const splitText = text.split("\n");

        if (!splitText) {
            return;
        }

        let lineNumber = 0;
        let currentLength = 0;
        let currentLineLength = 0;
        let findString = "";

        const regMention = /(?:^|(?<=\s))@(?:[\w\u0400-\u04FF]*|\b)/g;

        for (let i = 0; i < splitText.length - 1; i++) {
            currentLength += i === 0 ? splitText[i].length : splitText[i].length + 1;
            if (currentLength >= cursorIndex.index) {
                setCursorPosition({ line: lineNumber, character: cursorIndex.index });
                let currentPocCursor = cursorIndex.index + splitText[i].length - currentLength;
                let currentSplit = splitText[i].split(" ");
                for (let i = 0; i < currentSplit.length; i++) {
                    currentLineLength += currentSplit[i].length + 1;
                    if (currentLineLength >= currentPocCursor && currentSplit[i].includes("@")) {
                        if (currentSplit[i].match(regMention)) {
                            findString = currentSplit[i];
                            setMentionFilter(findString.slice(1, findString.length));
                        }
                    }
                }
            }
        }

        if (findString) {
            setShowChooseUser(true);
        } else {
            setShowChooseUser(false);
        }
    };

    // Проверка упоминания пользователя в тексте
    const checkMentionsUsersInText = (value: string) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, "text/html");
        const elementsWithDataId = doc.querySelectorAll("[data-id]");
        const userMention: number[] = [];

        elementsWithDataId.forEach((element) => {
            const userId = element.getAttribute("data-id");
            if (userId) {
                userMention.push(Number(userId));
            }
        });

        setUsersMention(userMention);
    };

    // Изменение в редакторе проходят через эту функцию
    const handleChange = (value: string) => {
        chekLengthText(value);

        if (workMode !== "editor") {
            if (quillInstanceRef.current) {
                checkMentionInText(quillInstanceRef.current);
            }
            checkMentionsUsersInText(value);
        }

        if (!addFile || checkAddFile === 0) {
            if (workMode !== "editor") {
                store.setBlockTextQuill(optionsQueryParams, value, window.location.pathname);
            }
            setContent(value);
            setSaveContent(value);
            setCheckAddFile(1);
        } else {
            if (workMode !== "editor") {
                store.setBlockTextQuill(optionsQueryParams, value, window.location.pathname);
            }
            setContent(saveContent);
            setAddFile(false);
            setCheckAddFile(0);
        }

        if (workMode === "editor") {
            callBackSave(value, usersMention);
        }
    };

    const uploadFile = async (where: "chat" | "task", file: File) => {
        try {
            handleShowLoader(true);
            if (where === "chat" && chatId) {
                if (chatId) {
                    const response = await store.fileService.uploadTempFile(file);
                    return getFileUrlFromId(response.data.id);
                }
            } else if (where === "task" && projectId) {
                const response = await store.fileService.uploadTempFile(file);
                return getFileUrlFromId(response.data.id);
            } else {
                throw new Error("Произошла ошибка при загрузке файла");
            }
        } catch (error: any) {
            const isFileTooLarge =
                error.response?.status === 413 || error.response?.data?.includes?.("413 Request Entity Too Large");
            if (isFileTooLarge) {
                showToast("Ошибка загрузки: Файл слишком большой");
            } else if (error.response?.data?.message) {
                showToast(error.response.data.message, error.response.data.details);
            } else {
                showToast("Ошибка загрузки: произошла неизвестная ошибка");
            }
            return undefined;
        } finally {
            handleShowLoader(false);
        }
    };

    // Событие драга при перемещении курсора в область редактора
    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(true);
    };

    // Событие драга при перемещении курсора за область редактора
    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(false);
    };

    // Дроп область для файлов
    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        handleShowLoader(true);
        setIsDragOver(false);
        event.preventDefault();
        event.stopPropagation();

        const quill = quillInstanceRef.current;
        if (!quill) return;

        const droppedFiles = Array.from(event.dataTransfer.files);
        if (!droppedFiles || droppedFiles.length === 0) return;

        const file = droppedFiles[0];

        try {
            let url;
            if (workMode === "edit" || workMode === "create") {
                url = await uploadFile("chat", file);
            } else if (workMode === "editor") {
                url = await uploadFile("task", file);
            }

            if (!url || url === window.location.href) {
                handleShowLoader(false);
                return;
            }

            const range = quill.getSelection(true);
            const index = range?.index ?? quill.getLength();

            if (file.type.startsWith("image/")) {
                quill.insertEmbed(index, "imageCustom", {
                    url: url,
                });
            } else {
                quill.insertEmbed(
                    index,
                    "fileUpload",
                    {
                        name: file.name,
                        url: url,
                    },
                    "user"
                );
            }

            quill.setSelection(index + 1, 0, "silent");
            handleShowLoader(false);
        } catch (error) {
            console.error("Ошибка загрузки изображения:", error);
            showToast("Ошибка при загрузке изображения");
            handleShowLoader(false);
        }
    };

    // Открытие окна для выбора пользователя
    const handleMentionPerson = () => {
        setShowChooseUser(true);
    };

    // Добавление файла
    const handleFileSelection = async () => {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "*/*";
        fileInput.multiple = false;
        fileInput.style.display = "none";

        try {
            const file = await new Promise<File>((resolve, reject) => {
                fileInput.onchange = (e) => {
                    const files = (e.target as HTMLInputElement).files;
                    if (!files?.length) {
                        reject(new Error("No file selected"));
                        return;
                    }
                    resolve(files[0]);
                };

                fileInput.oncancel = () => reject(new Error("File selection canceled"));
                document.body.appendChild(fileInput);
                fileInput.click();
            });

            const quill = quillInstanceRef.current;
            if (!quill) throw new Error("Quill instance not available");

            const range = quill.getSelection();
            const index = range?.index ?? quill.getLength();

            handleShowLoader(true);

            const uploadType = workMode === "editor" ? "task" : "chat";
            const url = await uploadFile(uploadType, file);

            if (!url || url === window.location.href) {
                throw new Error("Invalid upload URL");
            }

            quill.insertEmbed(index, "fileUpload", { name: file.name, url }, "user");

            quill.insertText(index + 1, "\n", "user");
            quill.setSelection(index + 2, 0, "silent");
        } catch (error) {
            if (error instanceof Error && error.message !== "File selection canceled") {
                console.error("File upload error:", error);
            }
        } finally {
            // Всегда убираем loader и очищаем DOM
            handleShowLoader(false);
            fileInput.remove();
        }
    };

    // Добавление изображения
    const handleImageSelection = () => {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "image/*";
        fileInput.style.display = "none";

        fileInput.onchange = async (e) => {
            handleShowLoader(true);
            const files = (e.target as HTMLInputElement).files;
            if (!files?.length) return;

            const quill = quillInstanceRef.current;
            if (!quill) return;

            const isEditorEmpty = () => {
                const contents = quill.getContents();
                return (
                    contents.ops.length === 1 &&
                    contents.ops[0].insert === "\n" &&
                    Object.keys(contents.ops[0]).length === 1
                );
            };

            const range = quill.getSelection();
            const index = range?.index || quill.getLength();

            try {
                const file = files[0];
                let url;
                if (workMode === "edit" || workMode === "create") {
                    url = await uploadFile("chat", file);
                } else if (workMode === "editor") {
                    url = await uploadFile("task", file);
                }

                if (!url || url === window.location.href) {
                    handleShowLoader(false);
                    return;
                }

                if (isEditorEmpty()) {
                    quill.setContents([], "user");
                }

                quill.insertEmbed(
                    index,
                    "imageCustom",
                    {
                        url: url,
                    },
                    "user"
                );

                quill.insertText(index + 1, "\n", "user");
                quill.setSelection(index + 2, 0, "silent");

                handleShowLoader(false);
            } catch (error) {
                handleShowLoader(false);
                console.error("Error:", error);
            } finally {
                document.body.removeChild(fileInput);
                handleShowLoader(false);
            }
        };

        document.body.appendChild(fileInput);
        fileInput.click();
    };

    // Добавление видео (ДОРАБОТАТЬ)
    const handleInsertVideo = () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "video/*");
        input.click();

        input.onchange = async () => {
            if (input.files && input.files[0]) {
                const file = input.files[0];
                const videoURL = URL.createObjectURL(file);

                const quill = quillInstanceRef.current;
                if (quill) {
                    const range = quill.getSelection(true);
                    const index = range ? range.index : 0;
                    quill.insertEmbed(index, "videoEmbed", videoURL, "user");
                    quill.setSelection(index + 1, 0, "silent");
                } else {
                    console.error("Quill editor not initialized");
                }
            }
        };
    };

    // Добавление упоминания пользователя
    const handleMention = (user: IFullUserInfo, triggerSymbol = "@") => {
        const quill = quillInstanceRef.current;
        if (!quill) {
            console.error("Quill editor not initialized");
            return;
        }

        const isEditorEmpty = () => {
            const contents = quill.getContents();
            return (
                contents.ops.length === 1 &&
                contents.ops[0].insert === "\n" &&
                Object.keys(contents.ops[0]).length === 1
            );
        };

        if (isEditorEmpty()) {
            quill.setContents([], "user");
        }

        const range = quill.getSelection(true);
        const index = range ? range.index : 0;
        let mentionStart = -1;
        let textBeforeCursor = quill.getText(0, index);

        mentionStart = textBeforeCursor.lastIndexOf(triggerSymbol);

        if (mentionStart > -1) {
            const textAfterSymbol = textBeforeCursor.substring(mentionStart + 1);
            const lengthToReplace = textAfterSymbol.length + 1;
            const deleteStartIndex = mentionStart;

            quill.deleteText(deleteStartIndex, lengthToReplace, "user");
            const adjustedIndex = deleteStartIndex;

            let id: number = user.id as number;
            let idPhoto: number = user?.photoId ? user!.photoId! : -1;
            let name: string = user.name as string;
            let surname: string = user.surname as string;

            const mentionData: MentionData = {
                id: String(id),
                photoId: String(idPhoto),
                surname: (name.length > 0 ? name + " " : "") + (surname.length > 0 ? surname : ""),
            };

            quill.insertEmbed(adjustedIndex, "mention", mentionData, "user");
            quill.setSelection(adjustedIndex + 1, 0, "silent");
        } else {
            let id: number = user.id as number;
            let idPhoto: number = user?.photoId ? user!.photoId! : -1;
            let name: string = user.name as string;
            let surname: string = user.surname as string;

            const mentionData: MentionData = {
                id: String(id),
                photoId: String(idPhoto),
                surname: (name.length > 0 ? name + " " : "") + (surname.length > 0 ? surname : ""),
            };

            quill.insertEmbed(index, "mention", mentionData, "user");
            quill.setSelection(index + 1, 0, "silent");
        }
    };

    // Вставка файла/изображения ctrl+v
    const handlePasteContent = async (event: ClipboardEvent) => {
        event.preventDefault();
        event.stopPropagation();
        handleShowLoader(true);

        const quill = quillInstanceRef.current;
        if (!quill) {
            handleShowLoader(false);
            return;
        }

        const clipboardItems = event.clipboardData?.items;
        if (!clipboardItems) {
            handleShowLoader(false);
            return;
        }

        const isEditorEmpty = () => {
            const contents = quill.getContents();
            return (
                contents.ops.length === 1 &&
                contents.ops[0].insert === "\n" &&
                Object.keys(contents.ops[0]).length === 1
            );
        };

        const range = quill.getSelection();
        const index = range?.index || quill.getLength();

        try {
            let file: File | null = null;
            for (let i = 0; i < clipboardItems.length; i++) {
                if (clipboardItems[i].kind === "file") {
                    file = clipboardItems[i].getAsFile();
                    if (file) break;
                }
            }

            if (!file) {
                handleShowLoader(false);
                return;
            }

            let url;
            if (workMode === "edit" || workMode === "create") {
                url = await uploadFile("chat", file);
            } else if (workMode === "editor") {
                url = await uploadFile("task", file);
            }

            if (!url || url === window.location.href) {
                handleShowLoader(false);
                return;
            }

            if (file.type.includes("image")) {
                quill.insertEmbed(
                    index,
                    "imageCustom",
                    {
                        url: url,
                    },
                    "user"
                );
            } else {
                quill.insertEmbed(
                    index,
                    "fileUpload",
                    {
                        name: file.name,
                        url: url,
                    },
                    "user"
                );
            }

            quill.insertText(index + 1, "\n", "user");
            quill.setSelection(index + 2, 0, "silent");
        } catch (error) {
            handleShowLoader(false);
            console.error("Error:", error);
        } finally {
            handleShowLoader(false);
        }
    };

    // Открытие редактора на максимум
    const handleFocus = () => {
        setFullShow((prevState) => !prevState);
    };

    // Сохранение нового сообщения
    const handleSave = () => {
        (async () => {
            if (!userAcess.create) {
                showToast("У вас нет прав для отправки сообщений в этот чат");
                return;
            }
            setFullShow(false);
            if (callBackSave) {
                let data = await callBackSave(content, usersMention);

                if (data !== undefined && data?.status !== "401") {
                    if (quillInstanceRef.current) {
                        quillInstanceRef.current.root.innerHTML = "";
                    }
                    setContent("");
                    store.clearCurrentBlocksTextQuill(optionsQueryParams, window.location.pathname);
                    urlClear(window.location.href, ["page", "entity", "action", "entityId", "checklistId"], navigate);
                }
            }
        })();
    };

    // Сохранение отредактированного сообщения
    const handleEditSave = () => {
        (async () => {
            if (!userAcess.edit) {
                showToast("У вас нет прав для редактирвоания сообщения в этом чате");
                return;
            }

            if (canSendEmptyMessage || content === undefined || content === "<p><br></p>") {
                showToast("Нельзя отправить пустое сообщение!");
                return;
            }
            setFullShow(false);
            if (callBackSave) {
                let data = await callBackSave(content, usersMention);

                if (data?.status !== "401") {
                    if (quillInstanceRef.current) {
                        quillInstanceRef.current.root.innerHTML = "";
                    }
                    setContent("");
                    store.clearAllBlocksTextQuill();
                    urlClear(window.location.href, ["page", "entity", "action", "entityId", "checklistId"], navigate);
                }
            }
        })();
    };

    // Отмена действия
    const handleClose = () => {
        setFullShow(false);
        callBackClose && callBackClose();
        if (quillInstanceRef.current) {
            quillInstanceRef.current.root.innerHTML = "";
        }
        setContent("");
        // store.setTextQuill("");
        urlClear(window.location.href, ["page", "entity", "action", "entityId", "checklistId"], navigate);
    };

    // Событие при открытие блока на максимум
    useEffect(() => {
        if (!fullShow) return;
        if (workMode === "editor") return;

        const editor = editorRef.current;
        if (!editor) return;

        const targetHeight = window.innerHeight * 0.7;

        const checkHeight = () => {
            if (editor.clientHeight >= targetHeight * 0.99) {
                centerEditor();
            } else {
                requestAnimationFrame(checkHeight);
            }
        };

        requestAnimationFrame(checkHeight);
    }, [fullShow]);

    // Открытие редактора по середине окна
    const centerEditor = () => {
        const editor = document.querySelector("#task-chat-wrapper-scrollable-part");

        if (!editor || !editorRef.current) return;

        const rectEditor = editor.getBoundingClientRect();
        const rectToContainer = editorRef.current.getBoundingClientRect();

        const scrollPosition =
            rectToContainer.top -
            rectEditor.top +
            editor.scrollTop -
            rectEditor.height / 2 +
            rectToContainer.height / 2;

        editor.scrollTo({
            top: scrollPosition,
            behavior: "smooth",
        });
    };

    // Эфект рабочей области
    useEffect(() => {
        if (workMode === "editor") {
            document.documentElement.style.setProperty("--display-style-change", "none");
        } else {
            document.documentElement.style.setProperty("--display-style-change", "inline-block");
        }

        return () => {
            document.documentElement.style.setProperty("--display-style-change", "inline-block");
        };
    }, [workMode]);

    // Блок выбора пользователей
    const mentionsUsersBlock = () => {
        return (
            <div className="input--input__block--show__persons">
                <UserMention
                    projectId={mention.projectId}
                    filter={mentionFilter}
                    mentionPersons={[]}
                    callBack={(user) => handleMention(user)}
                />
            </div>
        );
    };

    // Событие по нажатию на область редактирования
    const handleClickEvent = () => {
        urlFiller(
            window.location.href,
            {
                arguments: [
                    createArgumentIfDefined("action", optionsQueryParams.action),
                    createArgumentIfDefined("checklistId", optionsQueryParams.cheklistId),
                    createArgumentIfDefined("entity", optionsQueryParams.entity),
                    createArgumentIfDefined("entityId", optionsQueryParams.entityId),
                    createArgumentIfDefined("page", optionsQueryParams.page),
                ].filter(Boolean) as IArgument[],
                fragment: [],
            },
            false,
            navigate
        );
    };

    return (
        <div>
            {showChooseUser ? mentionsUsersBlock() : null}
            <div className={`text-editor_container ${disabled && "disabled"} ${isDragOver ? "drag-over" : ""}`}>
                {disabled && <div className="text-editor-disabled-overlay" />}
                <div
                    ref={editorRef}
                    onClick={handleClickEvent}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`${fullShow ? "text-editor_full-show" : ""} text-editor ql-container ql-snow`}
                />
                <div className="text-editor-length">
                    <a className={maxText ? "text-editor-length-error" : ""}>{curTextLength}</a>/<a>{MAX_TEXT}</a>
                </div>
                <div className="text-editor-buttons">
                    {mode === "edit" ? (
                        <>
                            <button
                                data-testid="button-text_editor_input-save"
                                className={`text-editor-buttons-send-div-approve ${
                                    userAcess.edit && !maxText ? "" : "text-editor-gray-button"
                                }`}
                                onClick={() =>
                                    !maxText ? handleEditSave() : showToast("Текс превысил допустимую длину")
                                }
                            >
                                Сохранить
                            </button>
                            <button
                                data-testid="button-text_editor_input-cancel"
                                className="text-editor-buttons-send-div-cansel"
                                onClick={handleClose}
                            >
                                Отмена
                            </button>
                        </>
                    ) : mode === "create" ? (
                        <button
                            data-testid="button-text_editor_input-send"
                            className={`text-editor-buttons-send-div-approve ${
                                userAcess.create && !maxText ? "" : "text-editor-gray-button"
                            }`}
                            onClick={() => (!maxText ? handleSave() : showToast("Текст превысил допустимую длину"))}
                        >
                            Отправить
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default TextEditor;
